import { FlipHorizontal2, FlipVertical2, RotateCw, Square } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MAX_MEDIA_EDIT_DIMENSION } from '@/shared/media/limits';

import {
  ASPECTS,
  DEFAULT_BACKGROUND,
  type AspectId,
  type ImageEditDraft,
  type ResizeTarget,
  type Size,
} from '../lib/image-edit';

export function CropTools({
  aspect,
  onAspect,
  onRotate,
  onFlipHorizontal,
  onFlipVertical,
}: {
  aspect: AspectId;
  onAspect: (aspect: AspectId) => void;
  onRotate: () => void;
  onFlipHorizontal: () => void;
  onFlipVertical: () => void;
}) {
  const { t } = useTranslation('media');
  const labelId = useId();
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <span id={labelId} className="text-sm font-medium">
          {t('editor.aspect')}
        </span>
        <div role="group" aria-labelledby={labelId} className="grid grid-cols-3 gap-1.5">
          {ASPECTS.map((id) => (
            <Button
              key={id}
              type="button"
              size="sm"
              variant={aspect === id ? 'secondary' : 'outline'}
              aria-pressed={aspect === id}
              onClick={() => onAspect(id)}
            >
              {t(`editor.aspects.${id}`)}
            </Button>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">{t('editor.orientation')}</span>
        <div className="flex flex-col gap-1.5">
          <Button type="button" size="sm" variant="outline" onClick={onRotate}>
            <RotateCw aria-hidden />
            {t('editor.rotate')}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={onFlipHorizontal}>
            <FlipHorizontal2 aria-hidden />
            {t('editor.flipHorizontal')}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={onFlipVertical}>
            <FlipVertical2 aria-hidden />
            {t('editor.flipVertical')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function parseSide(text: string): number | null {
  const value = Number(text);
  return Number.isInteger(value) && value >= 1 && value <= MAX_MEDIA_EDIT_DIMENSION ? value : null;
}

/**
 * Width and height of the finished file. The lock keeps the picture's shape; without it the size can
 * be any shape, and the whole picture fits inside with the background around it, so it never stretches.
 */
export function ResizeTools({
  source,
  draft,
  onResize,
}: {
  /** The cropped picture in real pixels. */
  source: Size;
  draft: ImageEditDraft;
  onResize: (resize: ResizeTarget | null) => void;
}) {
  const { t } = useTranslation('media');
  const widthId = useId();
  const heightId = useId();
  const lockId = useId();
  const backgroundId = useId();
  const current = draft.resize ?? source;
  const background = draft.resize?.background ?? DEFAULT_BACKGROUND;
  const [width, setWidth] = useState(String(current.width));
  const [height, setHeight] = useState(String(current.height));
  const [locked, setLocked] = useState(
    Math.abs(source.width * current.height - source.height * current.width) <=
      Math.max(source.width, source.height),
  );

  const commit = (next: Size, color = background) => {
    const unchanged = next.width === source.width && next.height === source.height;
    onResize(unchanged ? null : { ...next, background: color });
  };
  const changeWidth = (text: string) => {
    setWidth(text);
    const value = parseSide(text);
    if (value === null) return;
    if (!locked) {
      commit({ width: value, height: current.height });
      return;
    }
    const scaled = Math.max(1, Math.round((value * source.height) / source.width));
    if (scaled > MAX_MEDIA_EDIT_DIMENSION) return;
    setHeight(String(scaled));
    commit({ width: value, height: scaled });
  };
  const changeHeight = (text: string) => {
    setHeight(text);
    const value = parseSide(text);
    if (value === null) return;
    if (!locked) {
      commit({ width: current.width, height: value });
      return;
    }
    const scaled = Math.max(1, Math.round((value * source.width) / source.height));
    if (scaled > MAX_MEDIA_EDIT_DIMENSION) return;
    setWidth(String(scaled));
    commit({ width: scaled, height: value });
  };
  const fitToSquare = () => {
    const side = Math.min(Math.max(source.width, source.height), MAX_MEDIA_EDIT_DIMENSION);
    setLocked(false);
    setWidth(String(side));
    setHeight(String(side));
    commit({ width: side, height: side });
  };
  const toggleLock = (on: boolean) => {
    setLocked(on);
    if (!on) return;
    const scaled = Math.max(1, Math.round((current.width * source.height) / source.width));
    setHeight(String(scaled));
    commit({ width: current.width, height: scaled });
  };
  // Back to the size in effect when a field is left empty or out of range.
  const settle = () => {
    setWidth(String(current.width));
    setHeight(String(current.height));
  };

  const padded =
    Math.abs(source.width * current.height - source.height * current.width) >
    Math.max(source.width, source.height);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={widthId}>{t('editor.width')}</Label>
          <Input
            id={widthId}
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_MEDIA_EDIT_DIMENSION}
            value={width}
            onChange={(e) => changeWidth(e.target.value)}
            onBlur={settle}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={heightId}>{t('editor.height')}</Label>
          <Input
            id={heightId}
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_MEDIA_EDIT_DIMENSION}
            value={height}
            onChange={(e) => changeHeight(e.target.value)}
            onBlur={settle}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={lockId}
          checked={locked}
          onCheckedChange={(checked) => toggleLock(checked === true)}
        />
        <Label htmlFor={lockId}>{t('editor.lockRatio')}</Label>
      </div>
      <Button type="button" size="sm" variant="outline" onClick={fitToSquare}>
        <Square aria-hidden />
        {t('editor.fitToSquare')}
      </Button>
      <p className={cn('text-muted-foreground text-xs', padded && 'text-foreground')}>
        {t(locked ? 'editor.lockedHint' : 'editor.fitHint')}
      </p>
      {padded ? (
        <div className="flex items-center gap-2">
          <input
            id={backgroundId}
            type="color"
            value={background}
            className="border-input size-8 cursor-pointer rounded-md border bg-transparent p-0.5"
            onChange={(e) => commit(current, e.target.value)}
          />
          <Label htmlFor={backgroundId}>{t('editor.background')}</Label>
        </div>
      ) : null}
    </div>
  );
}
